import { useState } from "react";
import axios from "axios";
import { Loader2, Mail, X, CheckCircle2, Info } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Modal for the "Forgot password?" + "Forgot username?" recovery affordance.
// Backend always returns 200 to avoid email enumeration, so the UI shows the
// same "check your inbox" confirmation regardless of whether the email
// actually matches an account.
//
// Usage:
//   <ForgotPasswordModal open={open} onOpenChange={setOpen} initialEmail={email} />
export const ForgotPasswordModal = ({ open, onOpenChange, initialEmail = "" }) => {
  const [email, setEmail] = useState(initialEmail);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [showUsernameHelp, setShowUsernameHelp] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/auth/password-reset/request`, {
        email: email.trim(),
        origin_url: window.location.origin,
      });
      setSent(true);
    } catch {
      // Even on transport error we show the same success state so we don't
      // leak which emails are registered.
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    onOpenChange(false);
    // Reset after exit animation so the modal opens fresh next time.
    setTimeout(() => {
      setSent(false);
      setShowUsernameHelp(false);
    }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent
        className="sm:max-w-md rounded-2xl"
        data-testid="forgot-password-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-outfit text-[#1B2349] flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#3090A8]" />
            {sent ? "Check your inbox" : "Reset your password"}
          </DialogTitle>
          <DialogDescription className="text-[#718096]">
            {sent
              ? "If an account exists for that email, a reset link is on its way. The link expires in 30 minutes."
              : "Enter the email you signed up with. We'll send you a link to choose a new password."}
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="mt-2" data-testid="forgot-password-sent">
            <div className="flex items-start gap-3 bg-[#E7F4EA] rounded-xl p-4">
              <CheckCircle2 className="w-5 h-5 text-[#3FA363] mt-0.5 shrink-0" />
              <div className="text-sm text-[#1B2349] leading-relaxed">
                Didn't get it? Check spam, or come back in a minute and try
                again. Reset links expire in 30 minutes.
              </div>
            </div>
            <Button
              onClick={close}
              className="mt-4 w-full justify-center rounded-full bg-[#1B2349] text-white py-3 hover:bg-[#0F1838]"
              data-testid="forgot-password-close-btn"
            >
              Done
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-2" data-testid="forgot-password-form">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
              className="rounded-xl"
              data-testid="forgot-password-email"
            />

            <button
              type="button"
              onClick={() => setShowUsernameHelp((v) => !v)}
              className="mt-3 text-xs text-[#3090A8] hover:text-[#1B2349] underline underline-offset-2 flex items-center gap-1"
              data-testid="forgot-username-toggle"
            >
              <Info className="w-3.5 h-3.5" />
              Forgot which email you used?
            </button>
            {showUsernameHelp && (
              <p
                className="mt-2 text-xs text-[#4A5568] bg-[#FEF3E7] rounded-lg p-3 leading-relaxed"
                data-testid="forgot-username-help"
              >
                Your username is the email you signed up with. Try the email
                addresses you commonly use — Gmail, work, etc. If you signed in
                with Google, use the same Google account on the sign-in page
                instead of resetting a password.
              </p>
            )}

            <Button
              type="submit"
              disabled={busy || !email}
              className="mt-5 w-full justify-center rounded-full bg-[#1B2349] text-white py-3 hover:bg-[#0F1838]"
              data-testid="forgot-password-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
            </Button>
          </form>
        )}

        <button
          onClick={close}
          className="absolute top-4 right-4 rounded-full p-1 text-[#718096] hover:bg-slate-100"
          aria-label="Close"
          data-testid="forgot-password-x-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordModal;
